import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {formatTimeLabel} from '@/utils/textFormatting';
import WaveformCanvas from './WaveformCanvas';
import {useWaveform} from '@/hooks/useWaveform';
import {useTimelineDragging} from '@/hooks/timline/useTimelineDragging';
import {useTimelineScroll} from '@/hooks/timline/useTimelineScroll';
import {useSmoothTime} from '@/hooks/useSmoothTime';
import {useDarkMode} from '@/hooks/useDarkMode';
import {Subtitle} from "@/models/Subtitle";
import {assignLanes} from "@/utils/subtitleUtils";

interface TimelineViewProps {
    items: Subtitle[];
    currentTime: number;
    duration: number;
    onItemUpdate: (index: number, updatedItem: Subtitle) => void;
    onTimeSeek: (time: number) => void;
    isPlaying?: boolean;
    playbackSpeed?: number;
    onItemClick?: (index: number) => void;
    selectedItemIndex?: number | null;
    className?: string;
    audioFile?: File;
    isCollapsed?: boolean;
    onToggleCollapse?: () => void;
}

export default function TimelineView({
    items,
    currentTime,
    duration,
    onItemUpdate,
    onTimeSeek,
    isPlaying = false,
    playbackSpeed = 1.0,
    onItemClick,
    selectedItemIndex,
    className = '',
    audioFile,
    isCollapsed = false,
    onToggleCollapse
}: TimelineViewProps) {
    const containerRef = useRef<HTMLDivElement>(null);
    const isDark = useDarkMode();
    const {
        peaksCache,
        isLoading: isWaveformLoading,
        generateWaveformSegment,
        loadAudioFile
    } = useWaveform();
    const [zoom, setZoom] = useState(1);
    const [isDraggingSeekbar, setIsDraggingSeekbar] = useState(false);

    const pixelsPerSecond = 50 * zoom;
    const effectiveDuration = Math.max(duration, items.length > 0 ? items[items.length - 1].endTimeSeconds + 10 : 60);
    const timelineWidth = Math.max(effectiveDuration * pixelsPerSecond, 1000);
    const getTimeInterval = (zoom: number) => {
        if (zoom >= 4) return 1;
        if (zoom >= 2) return 2;
        if (zoom >= 1) return 5;
        if (zoom >= 0.5) return 10;
        if (zoom >= 0.25) return 30;
        return 60;
    };
    const timeInterval = getTimeInterval(zoom);

    const timeToX = (time: number) => time * pixelsPerSecond;
    const xToTime = (x: number) => x / pixelsPerSecond;

    const {isDragging, handleMouseDown} = useTimelineDragging({
        items,
        pixelsPerSecond,
        effectiveDuration,
        onItemUpdate,
        containerRef
    });

    const animatedCurrentTime = useSmoothTime({
        currentTime,
        isPlaying,
        isDragging: !!isDragging || isDraggingSeekbar,
        playbackSpeed
    });

    const handleTimelineClick = useCallback((e: React.MouseEvent) => {
        if (isDragging || isDraggingSeekbar) return;
        const rect = containerRef.current?.getBoundingClientRect();
        if (!rect) return;
        const x = e.clientX - rect.left + containerRef.current!.scrollLeft;
        const time = xToTime(x);
        onTimeSeek(Math.max(0, Math.min(time, effectiveDuration)));
    }, [isDragging, isDraggingSeekbar, xToTime, effectiveDuration, onTimeSeek]);

    const handleSeekbarMouseDown = useCallback((e: React.MouseEvent) => {
        e.stopPropagation();
        e.preventDefault();
        setIsDraggingSeekbar(true);
    }, []);

    useEffect(() => {
        if (!isDraggingSeekbar) return;

        const handleMouseMove = (e: MouseEvent) => {
            if (!containerRef.current) return;

            const rect = containerRef.current.getBoundingClientRect();
            const x = e.clientX - rect.left;
            const clampedX = Math.max(0, Math.min(x, rect.width));
            const scrollX = containerRef.current.scrollLeft;
            const time = xToTime(scrollX + clampedX);

            onTimeSeek(Math.max(0, Math.min(time, effectiveDuration)));
        };

        const handleMouseUp = () => {
            setIsDraggingSeekbar(false);
        };

        window.addEventListener('mousemove', handleMouseMove);
        window.addEventListener('mouseup', handleMouseUp);

        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isDraggingSeekbar, xToTime, onTimeSeek, effectiveDuration]);

    const generateWaveformForViewport = useCallback(async () => {
        if (!audioFile || !containerRef.current) return;

        const currentScrollLeft = containerRef.current.scrollLeft;
        const currentContainerWidth = containerRef.current.clientWidth;
        const currentPixelsPerSecond = 50 * zoom;
        const currentXToTime = (x: number) => x / currentPixelsPerSecond;

        const visibleStartTime = currentXToTime(currentScrollLeft);
        const visibleEndTime = currentXToTime(currentScrollLeft + currentContainerWidth);
        const visibleDuration = visibleEndTime - visibleStartTime;
        const bufferTime = visibleDuration * 0.5;
        const startTime = Math.max(0, visibleStartTime - bufferTime);
        const endTime = Math.min(duration, visibleEndTime + bufferTime);

        await generateWaveformSegment(startTime, endTime);
        // preload next segment
        if(endTime < duration) {
            const segmentDuration = endTime - startTime;
            await generateWaveformSegment(endTime, Math.min(duration, endTime + segmentDuration));
        }
    }, [audioFile, duration, zoom, generateWaveformSegment]);

    const {scrollLeft, containerWidth} = useTimelineScroll({
        containerRef,
        currentTime,
        pixelsPerSecond,
        isCollapsed,
        audioFile,
        isWaveformLoading,
        isDraggingSeekbar
    });

    const laneMap = useMemo(() => assignLanes(items), [items]);

    const visibleSubtitles = useMemo(() => {
        const buffer = containerWidth * 0.5;
        const visibleStartTime = xToTime(scrollLeft - buffer);
        const visibleEndTime = xToTime(scrollLeft + containerWidth + buffer);

        return items.filter((item, index) => {
            const itemStart = item.startTimeSeconds;
            const itemEnd = item.endTimeSeconds;
            const isVisible = itemStart < visibleEndTime && itemEnd > visibleStartTime;
            const isSelected = selectedItemIndex === index;
            return !item.deleted && (isVisible || isSelected);
        });
    }, [items, pixelsPerSecond, selectedItemIndex, scrollLeft, containerWidth, xToTime]);

    const subtitleRegions = useMemo(() => {
        return visibleSubtitles.map(item => ({
            startTime: item.startTimeSeconds,
            endTime: item.endTimeSeconds
        }));
    }, [visibleSubtitles]);

    useEffect(() => {
        if (audioFile && !isWaveformLoading && containerWidth > 0) {
            const timeoutId = setTimeout(generateWaveformForViewport, scrollLeft == 0 ? 0 : 100);
            return () => clearTimeout(timeoutId);
        }
    }, [audioFile, scrollLeft, containerWidth, generateWaveformForViewport, isWaveformLoading]);

    useEffect(() => {
        if (audioFile) {
            loadAudioFile(audioFile);
        }
    }, [audioFile, loadAudioFile]);

    const timeLabels = useMemo(() => {
        const result = [];
        const minSpacing = 60;
        const actualInterval = Math.max(timeInterval, minSpacing / pixelsPerSecond);

        let displayInterval = actualInterval;
        if (zoom >= 2 && actualInterval >= 2) {
            displayInterval = Math.max(0.5, actualInterval / 2);
        } else if (zoom >= 1 && actualInterval >= 5) {
            displayInterval = Math.max(1, actualInterval / 2);
        }

        const visibleStartTime = xToTime(scrollLeft);
        const visibleEndTime = xToTime(scrollLeft + containerWidth);

        for (let time = Math.max(0, Math.floor(visibleStartTime / displayInterval) * displayInterval);
             time <= Math.min(effectiveDuration, Math.ceil(visibleEndTime / displayInterval) * displayInterval);
             time += displayInterval) {
            result.push(Math.round(time * 10) / 10);
        }
        return result;
    }, [effectiveDuration, timeInterval, pixelsPerSecond, zoom, scrollLeft, containerWidth, xToTime]);

    return (
        <div className={`timeline-view bg-white dark:bg-gray-800 ${isCollapsed || !audioFile ? '' : ' shadow-[0_-1px_3px_rgba(0,0,0,0.03)] border-t border-gray-200 dark:border-gray-700'} ${className} relative group/timeline z-50`}>
            {/* Timeline controls */}
            {audioFile && (
                <>
                    {/* When collapsed, show only expand button in a small bar */}
                    <div
                        className={`flex justify-end bg-gray-50 dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 overflow-hidden transition-all duration-300 ease-in-out ${
                            isCollapsed ? 'max-h-10 py-1 opacity-100' : 'max-h-0 py-0 opacity-0'
                        }`}
                    >
                        {onToggleCollapse && (
                            <button
                                onClick={onToggleCollapse}
                                className="w-6 h-6 flex items-center justify-center mr-2 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-400 rounded"
                                title="Show timeline"
                            >
                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                          d="M5 15l7-7 7 7"/>
                                </svg>
                            </button>
                        )}
                    </div>

                    {/* When expanded, show all controls on hover */}
                    {!isCollapsed && (
                        <div className="absolute top-1 right-1 z-30 opacity-0 group-hover/timeline:opacity-100 transition-opacity duration-200 flex items-center space-x-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg px-1 py-1 shadow-lg">
                            <button
                                onClick={() => setZoom(prev => Math.max(0.1, prev * 0.8))}
                                className="w-6 h-6 flex items-center justify-center hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-400 rounded"
                                title="Zoom out"
                            >
                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4"/>
                                </svg>
                            </button>
                            <div className="text-xs text-gray-500 dark:text-gray-400 min-w-12 text-center notranslate">
                                {Math.round(zoom * 100)}%
                            </div>
                            <button
                                onClick={() => setZoom(prev => Math.min(5, prev * 1.25))}
                                className="w-6 h-6 flex items-center justify-center hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-400 rounded"
                                title="Zoom in"
                            >
                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                          d="M12 4v16m8-8H4"/>
                                </svg>
                            </button>
                            {onToggleCollapse && (
                                <button
                                    onClick={onToggleCollapse}
                                    className="w-6 h-6 flex items-center justify-center hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-400 rounded ml-2"
                                    title="Hide timeline"
                                >
                                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                              d="M19 9l-7 7-7-7"/>
                                    </svg>
                                </button>
                            )}
                        </div>
                    )}
                </>
            )}

            <div
                ref={containerRef}
                className={`timeline-container hidden lg:block overflow-x-auto overflow-y-hidden relative cursor-pointer transition-all duration-300 ease-in-out`}
                style={{
                    scrollBehavior: 'auto',
                    height: !isCollapsed && audioFile ? '95px' : '0px',
                    opacity: !isCollapsed && audioFile ? 1 : 0
                }}
                onClick={handleTimelineClick}
            >
                {
                    audioFile && (
                        <div className="relative overflow-hidden" style={{width: timelineWidth, height: '100%'}}>
                            {peaksCache.size > 0 && (
                                <WaveformCanvas
                                    peaksCache={peaksCache}
                                    duration={duration}
                                    pixelsPerSecond={pixelsPerSecond}
                                    currentTime={animatedCurrentTime}
                                    onTimeSeek={onTimeSeek}
                                    width={containerWidth}
                                    height={40}
                                    scrollLeft={scrollLeft}
                                    className="absolute bottom-0 pointer-events-none opacity-60"
                                    style={{left: scrollLeft, zIndex: 1}}
                                    subtitleRegions={subtitleRegions}
                                />
                            )}
                            {isWaveformLoading && (
                                <div
                                    className="absolute top-5 left-4 text-xs text-blue-600 dark:text-blue-400 bg-white dark:bg-gray-700 px-2 py-1 rounded shadow-sm z-10">
                                    Loading waveform...
                                </div>
                            )}

                            {timeLabels.map((time) => {
                                return (
                                    <div key={time} className="absolute top-0 select-none" style={{left: timeToX(time)}}>
                                        {/* Vertical tick mark */}
                                        <div className="absolute bg-gray-300 dark:bg-gray-600"
                                             style={{left: '0', top: '0', width: '1px', height: '7px', transform: 'translateX(-50%)'}} />

                                        <div className="absolute top-[7px] text-[0.7rem] font-mono text-gray-400 dark:text-gray-400 whitespace-nowrap"
                                             style={{transform: 'translateX(-50%)'}}>
                                            {formatTimeLabel(time)}
                                        </div>
                                    </div>
                                );
                            })}


                            <div
                                className="absolute top-0 z-20 flex flex-col items-center cursor-grab active:cursor-grabbing"
                                style={{
                                    left: timeToX(animatedCurrentTime),
                                    transform: 'translateX(-50%)',
                                    height: '100%'
                                }}
                                onMouseDown={handleSeekbarMouseDown}
                            >
                                <svg width="12" height="18" viewBox="0 0 12 18" fill="none"
                                     xmlns="http://www.w3.org/2000/svg">
                                    <mask id="grabber-mask" fill="white">
                                        <path
                                            d="M0 3C0 1.34314 1.34315 0 3 0H9C10.6569 0 12 1.34315 12 3V11.8287C12 12.7494 11.5772 13.6191 10.8531 14.1879L6 18L1.14686 14.1879C0.422795 13.6191 0 12.7494 0 11.8287V3Z"/>
                                    </mask>
                                    <path
                                        d="M0 3C0 1.34314 1.34315 0 3 0H9C10.6569 0 12 1.34315 12 3V11.8287C12 12.7494 11.5772 13.6191 10.8531 14.1879L6 18L1.14686 14.1879C0.422795 13.6191 0 12.7494 0 11.8287V3Z"
                                        fill={isDraggingSeekbar ? (isDark ? "#60A5FA" : "#090C14") : "white"}/>
                                    <path
                                        d="M6 18L4.76457 19.5728L6 20.5432L7.23543 19.5728L6 18ZM1.14686 14.1879L-0.0885728 15.7607L1.14686 14.1879ZM10.8531 14.1879L12.0886 15.7607L10.8531 14.1879ZM3 2H9V-2H3V2ZM10 3V11.8287H14V3H10ZM2 11.8287V3H-2V11.8287H2ZM9.61771 12.6151L4.76457 16.4272L7.23543 19.5728L12.0886 15.7607L9.61771 12.6151ZM7.23543 16.4272L2.38228 12.6151L-0.0885728 15.7607L4.76457 19.5728L7.23543 16.4272ZM-2 11.8287C-2 13.3632 -1.29534 14.8128 -0.0885728 15.7607L2.38228 12.6151C2.14093 12.4255 2 12.1356 2 11.8287H-2ZM10 11.8287C10 12.1356 9.85907 12.4255 9.61771 12.6151L12.0886 15.7607C13.2953 14.8128 14 13.3632 14 11.8287H10ZM9 2C9.55228 2 10 2.44772 10 3H14C14 0.238577 11.7614 -2 9 -2V2ZM3 -2C0.238579 -2 -2 0.23857 -2 3H2C2 2.44771 2.44771 2 3 2V-2Z"
                                        fill="#090C14" mask="url(#grabber-mask)"/>
                                </svg>
                                <div
                                    className="w-0.5 bg-gray-600 flex-grow"
                                />
                            </div>

                            {visibleSubtitles.map((item) => {
                                const itemIndex = items.findIndex(i => i.id === item.id);
                                const left = timeToX(item.startTimeSeconds);
                                const width = timeToX(item.endTimeSeconds - item.startTimeSeconds);

                                const {lane, laneCount, nextSameLaneStart} = laneMap.get(item.id)!;
                                const laneGap = 2;
                                const laneHeight = (55 - laneGap * (laneCount - 1)) / laneCount;
                                const laneBottom = lane * (laneHeight + laneGap);

                                const maxWidth = nextSameLaneStart === undefined
                                    ? Infinity
                                    : timeToX(nextSameLaneStart) - left;
                                const constrainedWidth = Math.min(Math.max(width, 20), maxWidth);

                                return (
                                    <div
                                        key={item.id}
                                        className="absolute rounded-lg cursor-move group shadow-sm"
                                        style={{
                                            left,
                                            width: constrainedWidth,
                                            bottom: laneBottom,
                                            height: laneHeight,
                                            boxSizing: 'border-box',
                                            backgroundColor: isDark
                                                ? `hsla(210, 60%, ${selectedItemIndex === itemIndex ? '35%' : '30%'}, ${selectedItemIndex === itemIndex ? 0.1 : 0.2})`
                                                : `hsla(210, 85%, 89.5%, ${selectedItemIndex === itemIndex ? 0.7 : 0.6})`,
                                            border: isDark
                                                ? `1px solid hsla(210, ${selectedItemIndex === itemIndex ? 70 : 50}%, ${selectedItemIndex === itemIndex ? '50%' : '45%'})`
                                                : `1px solid hsla(210, ${selectedItemIndex === itemIndex ? 90 : 70}%, 57%)`,
                                            zIndex: 10
                                        }}
                                        onMouseDown={(e) => handleMouseDown(e, itemIndex, 'move')}
                                        onClick={(e) => {
                                            if (!isDragging && onItemClick) {
                                                e.stopPropagation();
                                                onItemClick(itemIndex);
                                            }
                                        }}
                                    >
                                        <div
                                            className="px-2 py-0.5 text-xs text-blue-900 dark:text-blue-100 h-full flex items-start"
                                            dir="auto"
                                            style={{
                                                unicodeBidi: 'isolate',
                                                textAlign: 'start'
                                            }}
                                        >
                                    <span
                                        dir="auto"
                                        style={{
                                            unicodeBidi: 'isolate',
                                            overflow: 'hidden',
                                            whiteSpace: 'nowrap',
                                            textOverflow: 'ellipsis',
                                            display: 'block',
                                            width: '100%'
                                        }}
                                    >
                                        {item.text}
                                    </span>
                                        </div>

                                        <div
                                            className="absolute left-1 top-1/2 transform -translate-y-1/2 w-1 h-6 bg-white dark:bg-gray-200 rounded-full cursor-w-resize opacity-0 group-hover:opacity-100 shadow-sm ring-1 ring-gray-300 dark:ring-gray-600"
                                            onMouseDown={(e) => {
                                                e.stopPropagation();
                                                handleMouseDown(e, itemIndex, 'resize-start');
                                            }}
                                        />

                                        <div
                                            className="absolute right-1 top-1/2 transform -translate-y-1/2 w-1 h-6 bg-white dark:bg-gray-200 rounded-full cursor-e-resize opacity-0 group-hover:opacity-100 shadow-sm ring-1 ring-gray-300 dark:ring-gray-600"
                                            onMouseDown={(e) => {
                                                e.stopPropagation();
                                                handleMouseDown(e, itemIndex, 'resize-end');
                                            }}
                                        />
                                    </div>
                                );
                            })}
                        </div>
                    )
                }
            </div>
        </div>
    );
}
